import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { trackRouter } from 'vue-gtag-next';
import { createRouter, createWebHistory } from 'vue-router';
import { isEmpty } from '@/common/validators';
import { decryptionTag } from '@/common/cryptionTags';
import http from '@/common/http';
import store from '@/store/index.js';
import constants from '@/config/constants';

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import(/* webpackChunkName: "login" */ '../views/user/Login.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/resetPasswordVerify',
    name: 'ResetPasswordVerify',
    component: () => import(/* webpackChunkName: "resetPasswordVerify" */ '../views/user/ResetPasswordVerify.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/resetPassword',
    name: 'ResetPassword',
    component: () => import(/* webpackChunkName: "resetPassword" */ '../views/user/ResetPassword.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/registAddInfoConfirm',
    name: 'RegistAddInfoConfirm',
    component: () => import(/* webpackChunkName: "registAddInfoConfirm" */ '../views/user/RegistAddInfoConfirm.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/userRegister',
    name: 'UserRegister',
    redirect: '/userRegister/agreeTerms',
    component: () => import(/* webpackChunkName: "userRegister" */ '../views/user/UserRegister.vue'),
    meta: { requiresAuth: false },
    children: [
      {
        path: 'agreeTerms',
        name: 'AgreeTerms',
        component: () => import(/* webpackChunkName: "agreeTerms" */ '../views/AgreeTerms.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'agreePrivacyPolicy',
        name: 'AgreePrivacyPolicy',
        component: () => import(/* webpackChunkName: "agreePrivacyPolicy" */ '../views/AgreePrivacyPolicy.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'userInfo',
        name: 'UserInfo',
        component: () => import(/* webpackChunkName: "userInfo" */ '../views/user/UserInfo.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'userInfoConfirmation',
        name: 'UserInfoConfirmation',
        component: () => import(/* webpackChunkName: "userInfoConfirmation" */ '../views/user/UserInfoConfirmation.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'verify',
        name: 'Verify',
        component: () => import(/* webpackChunkName: "verify" */ '../views/user/Verify.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'planApplication',
        name: 'UserRegisterPlanApplication',
        component: () => import(/* webpackChunkName: "userRegisterPlanApplication" */ '../views/user/UserRegisterPlanApplication.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'successSignUp',
        name: 'SuccessSignUp',
        component: () => import(/* webpackChunkName: "successSignUp" */ '../views/user/SuccessSignUp.vue'),
        meta: { requiresAuth: false }
      },
      {
        path: 'successSignUpApp',
        name: 'SuccessSignUpApp',
        component: () => import(/* webpackChunkName: "successSignUpApp" */ '../views/user/SuccessSignUpApp.vue'),
        meta: { requiresAuth: false }
      }
    ]
  },
  {
    path: '/userReference',
    name: 'UserReference',
    component: () => import(/* webpackChunkName: "userReference" */ '../views/user/UserReference.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/userMaintenance',
    name: 'UserMaintenance',
    component: () => import(/* webpackChunkName: "userMaintenance" */ '../views/user/UserMaintenance.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/updatePassword',
    name: 'UpdatePassword',
    component: () => import(/* webpackChunkName: "updatePassword" */ '../views/user/UpdatePassword.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/updateMailAddress',
    name: 'UpdateMailAddress',
    component: () => import(/* webpackChunkName: "updateMailAddress" */ '../views/user/UpdateMailAddress.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/verifyByUpdate',
    name: 'VerifyByUpdate',
    component: () => import(/* webpackChunkName: "verifyByUpdate" */ '../views/user/VerifyByUpdate.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/deleteAccountConfirm',
    name: 'DeleteAccountConfirm',
    component: () => import(/* webpackChunkName: "deleteAccountConfirm" */ '../views/user/DeleteAccountConfirm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/deleteAccountSuccess',
    name: 'DeleteAccountSuccess',
    component: () => import(/* webpackChunkName: "deleteAccountSuccess" */ '../views/user/DeleteAccountSuccess.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/serviceStatus',
    name: 'ServiceStatus',
    component: () => import(/* webpackChunkName: "serviceStatus" */ '../views/user/ServiceStatus.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/error',
    name: 'Error',
    component: () => import(/* webpackChunkName: "error" */ '../views/Error.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/planApplication',
    name: 'PlanApplication',
    component: () => import(/* webpackChunkName: "planApplication" */ '../views/user/PlanApplication.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/unregistPlanConfirm',
    name: 'UnregistPlanConfirm',
    component: () => import(/* webpackChunkName: "unregistPlanConfirm" */ '../views/user/UnregistPlanConfirm.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/unregistPlanSuccess',
    name: 'UnregistPlanSuccess',
    component: () => import(/* webpackChunkName: "unregistPlanSuccess" */ '../views/user/UnregistPlanSuccess.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/reagreeTerms',
    name: 'ReagreeTerms',
    component: () => import(/* webpackChunkName: "reagreeTerms" */ '../views/ReagreeTerms.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/reagreePrivacyPolicy',
    name: 'ReagreePrivacyPolicy',
    component: () => import(/* webpackChunkName: "reagreePrivacyPolicy" */ '../views/ReagreePrivacyPolicy.vue'),
    meta: { requiresAuth: true }
  },
  { path: '/:pathMatch(.*)', redirect: '/userReference' }
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
});

router.beforeEach(async to => {
  try {
    let isLogin = false;
    // ストアのユーザ情報の更新フラグ
    let isUserReload = true;
    // 利用規約再同意フラグ
    let usersReagree = false;
    // 個人情報の取扱い再同意フラグ
    let personalReagree = false;

    // ストアのユーザIDを取得
    let uid = store.getters['user/user_id'];

    // Getパラメータにタグ情報(暗号化テナントコード）が存在する場合
    if (!isEmpty(to.query.tags)) {
      // リダイレクト先がある場合ページ情報を設定する
      let redirect = null;

      // 暗号化タグ情報
      // vueの仕様でtagsの値がデコードされた値になるため再度エンコードを行う
      let encryptionTags = encodeURIComponent(to.query.tags);

      // 復号化タグ情報
      // Getパラメータにタグ情報が存在する場合、タグを復号化する。
      let decryptionTags = await decryptionTag(encryptionTags);

      // URLパラメータに外部連携情報が含まれている場合
      let paramExkey = null;
      if (!isEmpty(to.query.exkey)) {
        paramExkey = to.query.exkey;
      }

      if (decryptionTags) {
        // テナントの状態確認
        let param = {
          tenantCode: decryptionTags,
          exkey: paramExkey,
          NoLoginAccess: true
        };
        await http
          .post(constants.API_USER_REGIST_ADD_INFO_CHECK_URI, param)
          .then(response => {
            // ストアにテナント情報、外部連携パラメータを登録
            const addInfoData = {
              tags: null,
              exkey: null
            };

            if (response.data.check_result === true) {
              if (to.query.tags) {
                addInfoData.tags = to.query.tags;
              }
              if (paramExkey) {
                addInfoData.exkey = paramExkey;
              }
              // プラン紐づけ画面へリダイレクト
              redirect = { name: 'RegistAddInfoConfirm' };
            }

            // ストアの値を更新
            // チェック結果が有効の場合、タグ・外部連携パラメータを保存
            // チェック結果が無効の場合、クリア
            store.dispatch('addinfo/setAddInfo', addInfoData);
          })
          .catch(e => {
            // 共通エラー処理を行う
            throw e;
          });

        // リダイレクト先の設定がある場合、設定された画面に遷移する
        if (!isEmpty(redirect)) return redirect;
      }
    }

    // 認証情報の確認
    await getCurrentUser()
      .then(async () => {
        isLogin = true;
        // ユーザープロフィール参照画面に遷移する際のみ、退会状態orデータ削除状態のユーザはログアウトする
        if (to.name === 'UserReference') {
          let isLogout = false;
          try {
            const response = await http.get(constants.API_USER_DELETED_URI);
            if (response.data.userDeleted === true) {
              isLogout = true;
            }
          } catch (error) {
            // ユーザー削除済みチェックでエラーが発生した場合もログアウトする
            isLogout = true;
          }

          if (isLogout) {
            await signOut();
            store.dispatch('user/clearUser');
            sessionStorage.clear();
            // ログイン状態を未ログインに変更
            isLogin = false;
            // ストアクリアのため、ユーザーIDを初期化
            uid = null;
          }
        }
      })
      .catch(() => {
        isLogin = false;
      });

    if (!isLogin && to.meta.requiresAuth) {
      // 未ログインで認証が必要な画面を表示しようとした場合、遷移先をログイン画面に書き換える
      return { name: 'Login' };
    } else if (isLogin && uid == null) {
      // ログイン済かつストアにユーザIDが無い場合、情報の取得を行う
      await store.dispatch('user/getRequest').then(response => {
        isUserReload = false;
        usersReagree = response.usersReagree;
        personalReagree = response.personalReagree;
      });
    }

    if (to.name === 'UserReference') {
      // ユーザ情報の再読み込み処理
      if (isUserReload) {
        // ログインユーザのストアのユーザ情報を更新
        await store.dispatch('user/getRequest').then(response => {
          isUserReload = false;
          usersReagree = response.usersReagree;
          personalReagree = response.personalReagree;
        });
      }
      if (isLogin) {
        if (usersReagree === true) {
          // 会員に関する規約の再同意が必要な場合
          // 会員に関する規約の再同意画面へ遷移
          return { name: 'ReagreeTerms' };
        } else if (personalReagree === true) {
          // 個人に関する規約の再同意が必要な場合
          // 個人に関する規約の再同意画面へ遷移
          return { name: 'ReagreePrivacyPolicy' };
        }
      }
    }
  } catch (error) {
    // 共通エラー処理
    return { name: 'Error' };
  }
});

trackRouter(router);

export default router;
